import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { listAiToolPlugins } from '../../ai-tool-plugins/services/toolPluginService';
import type { AiToolPluginDefinition } from '../../ai-tool-plugins/types/ToolPlugin';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import { getToolUseCaseAssignments } from '../../tools/services/toolUseCaseAssignmentService';
import {
  connectMcpCatalogProvider,
  createMcpServer,
  deleteMcpServer,
  deleteMcpTool,
  disconnectMcpOAuth,
  discoverMcpServer,
  importMcpTools,
  listMcpCatalog,
  listMcpServers,
  listMcpTools,
  previewMcpTool,
  startMcpOAuth,
  testMcpServer,
  updateMcpServer,
  updateMcpTool,
  type McpCatalogProvider,
  type McpServer,
  type McpTool,
} from '../services/mcpService';
import {
  closeMcpOAuthPopup,
  openMcpOAuthPopup,
  readMcpOAuthMessage,
  sendPopupTo,
} from '../utils/mcpOAuthPopup';
import { McpBrandMark } from './McpBrandMark';
import {
  McpServerForm,
  emptyServerForm,
  loadServerForm,
  toServerPayload,
} from './McpServerForm';
import {
  AlertIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  ImportIcon,
  PlayIcon,
  PlugIcon,
  PlusIcon,
  PulseIcon,
  RadarIcon,
  SearchIcon,
  ShieldIcon,
  ToolboxIcon,
  TrashIcon,
} from './McpIcons';
import styles from './McpToolsPage.module.css';

type PaneMode = 'catalog' | 'create' | 'server';
type WorkspaceTab = 'tools' | 'settings';
type ToolFilter = 'all' | 'published' | 'draft';
type ConnectionHealth = 'ok' | 'warn' | 'error' | 'idle';

const endpointHost = (endpointUrl: string): string => {
  try {
    return new URL(endpointUrl).host;
  } catch {
    return endpointUrl;
  }
};

const connectionHealth = (server: McpServer): ConnectionHealth => {
  if (server.lastTestStatus === 'failed') {
    return 'error';
  }

  if (server.authType === 'oauth' && !server.oauthConnected) {
    return 'warn';
  }

  return server.lastTestStatus ? 'ok' : 'idle';
};

export const McpToolsPage: React.FC = () => {
  const { t } = useTranslation(['mcp', 'common', 'tools', 'toolPlugins']);
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission(PermissionCodes.mcpConnectorsManage);

  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState<McpServer[]>([]);
  const [tools, setTools] = useState<McpTool[]>([]);
  const [catalog, setCatalog] = useState<McpCatalogProvider[]>([]);
  const [toolPlugins, setToolPlugins] = useState<AiToolPluginDefinition[]>([]);

  const [paneMode, setPaneMode] = useState<PaneMode>('catalog');
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>('tools');
  const initializedRef = useRef(false);

  const [serverQuery, setServerQuery] = useState('');
  const [catalogQuery, setCatalogQuery] = useState('');
  const [toolQuery, setToolQuery] = useState('');
  const [toolFilter, setToolFilter] = useState<ToolFilter>('all');

  const [serverForm, setServerForm] = useState(emptyServerForm);
  const [creatingServer, setCreatingServer] = useState(false);
  const [savingServer, setSavingServer] = useState(false);
  const [testing, setTesting] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  const [connectingProviderId, setConnectingProviderId] = useState<string | null>(null);
  const [providerFields, setProviderFields] = useState<Record<string, Record<string, string>>>({});
  const [tokenPromptProviderIds, setTokenPromptProviderIds] = useState<string[]>([]);
  const [providerTokens, setProviderTokens] = useState<Record<string, string>>({});
  const [oauthClientPromptProviderIds, setOauthClientPromptProviderIds] = useState<string[]>([]);
  const [providerOAuthClients, setProviderOAuthClients] = useState<Record<string, { clientId: string; clientSecret: string }>>({});
  const [providerOAuthRedirectUris, setProviderOAuthRedirectUris] = useState<Record<string, string>>({});
  const [copiedOAuthRedirectProviderId, setCopiedOAuthRedirectProviderId] = useState<string | null>(null);
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [oauthPopupOpen, setOauthPopupOpen] = useState(false);
  const oauthPopupRef = useRef<Window | null>(null);

  const [selectedRemoteTools, setSelectedRemoteTools] = useState<string[]>([]);
  const [importAsPublished, setImportAsPublished] = useState(false);
  const [importAssignedUseCaseIds, setImportAssignedUseCaseIds] = useState<AiPromptUseCaseId[]>(
    [...DEFAULT_ASSIGNED_USE_CASE_IDS],
  );
  const [importing, setImporting] = useState(false);
  const [showImportOptions, setShowImportOptions] = useState(false);

  const [selectedImportedToolIds, setSelectedImportedToolIds] = useState<string[]>([]);
  const [bulkAssignedUseCaseIds, setBulkAssignedUseCaseIds] = useState<AiPromptUseCaseId[]>(
    [...DEFAULT_ASSIGNED_USE_CASE_IDS],
  );
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [showBulkOptions, setShowBulkOptions] = useState(false);

  const [previewTargetToolId, setPreviewTargetToolId] = useState<string | null>(null);
  const [previewArgs, setPreviewArgs] = useState<Record<string, string>>({});
  const [previewResult, setPreviewResult] = useState('');
  const [previewingToolId, setPreviewingToolId] = useState<string | null>(null);
  const [toolAssignedUseCaseIds, setToolAssignedUseCaseIds] = useState<AiPromptUseCaseId[]>([]);
  const [savingToolId, setSavingToolId] = useState<string | null>(null);

  /* ------------------------------------------------------------ derived */

  const selectedServer = useMemo(
    () => servers.find((server) => server.serverId === selectedServerId) ?? null,
    [servers, selectedServerId],
  );

  const serverTools = useMemo(
    () => tools.filter((tool) => tool.serverId === selectedServerId),
    [tools, selectedServerId],
  );

  const previewTargetTool = useMemo(
    () => serverTools.find((tool) => tool.toolId === previewTargetToolId) ?? null,
    [previewTargetToolId, serverTools],
  );

  const publishedToolCount = useMemo(
    () => tools.filter((tool) => tool.isPublished).length,
    [tools],
  );

  const filteredServers = useMemo(() => {
    const query = serverQuery.trim().toLowerCase();
    if (!query) {
      return servers;
    }

    return servers.filter((server) => (
      server.displayName.toLowerCase().includes(query)
      || server.endpointUrl.toLowerCase().includes(query)
    ));
  }, [serverQuery, servers]);

  const filteredCatalog = useMemo(() => {
    const query = catalogQuery.trim().toLowerCase();
    if (!query) {
      return catalog;
    }

    return catalog.filter((provider) => (
      provider.displayName.toLowerCase().includes(query)
      || provider.description.toLowerCase().includes(query)
    ));
  }, [catalog, catalogQuery]);

  const visibleTools = useMemo(() => {
    const query = toolQuery.trim().toLowerCase();

    return serverTools.filter((tool) => {
      if (toolFilter === 'published' && !tool.isPublished) {
        return false;
      }

      if (toolFilter === 'draft' && tool.isPublished) {
        return false;
      }

      if (!query) {
        return true;
      }

      return tool.displayName.toLowerCase().includes(query)
        || tool.remoteToolName.toLowerCase().includes(query)
        || tool.kernelFunctionName.toLowerCase().includes(query);
    });
  }, [serverTools, toolFilter, toolQuery]);

  const catalogServersByProvider = useMemo(() => {
    const map = new Map<string, McpServer>();
    servers.forEach((server) => {
      if (server.catalogProviderId) {
        map.set(server.catalogProviderId.toLowerCase(), server);
      }
    });
    return map;
  }, [servers]);

  const importedRemoteNames = useMemo(
    () => new Set(serverTools.map((tool) => tool.remoteToolName)),
    [serverTools],
  );

  const pluginNamesByFunctionId = useMemo(() => {
    const map = new Map<string, string[]>();
    toolPlugins.forEach((plugin) => {
      plugin.memberFunctionIds.forEach((functionId) => {
        map.set(functionId, [...(map.get(functionId) ?? []), plugin.kernelPluginName]);
      });
    });
    return map;
  }, [toolPlugins]);

  const discoveredTools = selectedServer?.discoveredTools ?? [];
  const newlyDiscoveredCount = discoveredTools.filter(
    (tool) => !importedRemoteNames.has(tool.name),
  ).length;

  const isCatalogServer = Boolean(selectedServer?.catalogProviderId);
  const allDiscoveredSelected = discoveredTools.length > 0
    && discoveredTools.every((tool) => selectedRemoteTools.includes(tool.name));
  const allVisibleToolsSelected = visibleTools.length > 0
    && visibleTools.every((tool) => selectedImportedToolIds.includes(tool.toolId));

  /* -------------------------------------------------------------- loading */

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [serverRows, toolRows, catalogRows, pluginRows] = await Promise.all([
        listMcpServers(),
        listMcpTools(),
        listMcpCatalog().catch(() => [] as McpCatalogProvider[]),
        listAiToolPlugins().catch(() => [] as AiToolPluginDefinition[]),
      ]);

      setServers(serverRows);
      setTools(toolRows);
      setCatalog(catalogRows);
      setToolPlugins(pluginRows);
      setSelectedServerId((current) => current ?? serverRows[0]?.serverId ?? null);

      // The first load decides whether the pane starts on the catalog or a server.
      if (!initializedRef.current) {
        initializedRef.current = true;
        setPaneMode(serverRows.length > 0 ? 'server' : 'catalog');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (paneMode === 'create') {
      return;
    }

    const server = servers.find((item) => item.serverId === selectedServerId) ?? null;
    setServerForm(loadServerForm(server));
  }, [paneMode, servers, selectedServerId]);

  useEffect(() => {
    if (!canManage && workspaceTab === 'settings') {
      setWorkspaceTab('tools');
    }
  }, [canManage, workspaceTab]);

  useEffect(() => {
    if (!previewTargetTool) {
      setToolAssignedUseCaseIds([]);
      return undefined;
    }

    let cancelled = false;
    void getToolUseCaseAssignments(previewTargetTool.functionId)
      .then((assignedUseCaseIds) => {
        if (!cancelled) {
          setToolAssignedUseCaseIds(assignedUseCaseIds);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToolAssignedUseCaseIds([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [previewTargetTool]);

  /* ---------------------------------------------------------- oauth popup */

  const applyOAuthResult = useCallback((status: string, serverId: string) => {
    if (serverId) {
      setSelectedServerId(serverId);
      setPaneMode('server');
      setWorkspaceTab('tools');
    }

    const messageKey = status === 'success'
      ? 'oauthConnected'
      : status === 'no_refresh_token'
        ? 'oauthNoRefreshToken'
        : 'oauthFailed';

    showToast(t(messageKey), status === 'success' ? 'success' : 'error');
    void loadData();
  }, [loadData, showToast, t]);

  const releaseOAuthPopup = useCallback(() => {
    closeMcpOAuthPopup(oauthPopupRef.current);
    oauthPopupRef.current = null;
    setOauthPopupOpen(false);
    setConnectingProviderId(null);
    setConnectingOAuth(false);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const result = readMcpOAuthMessage(event);
      if (!result) return;

      releaseOAuthPopup();
      applyOAuthResult(result.status, result.serverId);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [applyOAuthResult, releaseOAuthPopup]);

  // A popup closed by hand sends no message, so the buttons are re-enabled by polling.
  useEffect(() => {
    if (!oauthPopupOpen) return undefined;

    const timer = window.setInterval(() => {
      if (!oauthPopupRef.current || oauthPopupRef.current.closed) {
        releaseOAuthPopup();
      }
    }, 500);

    return () => window.clearInterval(timer);
  }, [oauthPopupOpen, releaseOAuthPopup]);

  // Fallback for browsers that blocked the popup and ran the flow in this tab instead.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('mcpOAuth');
    if (!status) return;

    applyOAuthResult(status, params.get('serverId') ?? '');

    params.delete('mcpOAuth');
    params.delete('serverId');
    const nextQuery = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`);
  }, [applyOAuthResult]);

  useEffect(() => {
    if (!previewTargetToolId) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewTargetToolId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewTargetToolId]);

  /* ------------------------------------------------------------ navigation */

  const resetWorkspaceSelections = () => {
    setSelectedRemoteTools([]);
    setSelectedImportedToolIds([]);
    setImportAsPublished(false);
    setShowImportOptions(false);
    setShowBulkOptions(false);
    setPreviewTargetToolId(null);
    setToolQuery('');
    setToolFilter('all');
  };

  const openCatalogPane = () => {
    setPaneMode('catalog');
    resetWorkspaceSelections();
  };

  const startCreateServer = () => {
    setPaneMode('create');
    setSelectedServerId(null);
    setServerForm(emptyServerForm);
    resetWorkspaceSelections();
  };

  const handleSelectServer = (serverId: string) => {
    setSelectedServerId(serverId);
    setPaneMode('server');
    setWorkspaceTab('tools');
    resetWorkspaceSelections();
  };

  /* --------------------------------------------------------- catalog flow */

  const setProviderField = (providerId: string, fieldName: string, value: string) => {
    setProviderFields((current) => ({
      ...current,
      [providerId]: { ...(current[providerId] ?? {}), [fieldName]: value },
    }));
  };

  const handleConnectProvider = async (
    provider: McpCatalogProvider,
    options: { personalAccessToken?: string; oauthClientId?: string; oauthClientSecret?: string } = {},
  ) => {
    if (!canManage) return;

    const fields = { ...(providerFields[provider.providerId] ?? {}) };
    provider.fields.forEach((field) => {
      if (!(fields[field.name] ?? '').trim() && field.allowedValues.length > 0) {
        fields[field.name] = field.placeholder || field.allowedValues[0];
      }
    });
    const missingField = provider.fields.find((field) => !(fields[field.name] ?? '').trim());
    if (!options.personalAccessToken && missingField) {
      showToast(t('catalogFieldRequired', { field: missingField.name }), 'error');
      return;
    }

    const oauthClientId = (options.oauthClientId ?? '').trim();
    if (oauthClientPromptProviderIds.includes(provider.providerId) && !oauthClientId) {
      showToast(t('catalogOAuthClientIdRequired'), 'error');
      return;
    }

    setConnectingProviderId(provider.providerId);
    const popup = openMcpOAuthPopup();
    oauthPopupRef.current = popup;

    try {
      const result = await connectMcpCatalogProvider(provider.providerId, {
        fields: provider.fields.length > 0 ? fields : undefined,
        personalAccessToken: options.personalAccessToken,
        oauthClientId: oauthClientId || undefined,
        oauthClientSecret: options.oauthClientSecret?.trim() || undefined,
      });

      if (result.authorizeUrl) {
        setOauthClientPromptProviderIds((current) => current.filter((item) => item !== provider.providerId));
        if (sendPopupTo(popup, result.authorizeUrl)) {
          setOauthPopupOpen(true);
        } else {
          window.location.assign(result.authorizeUrl);
        }
        return;
      }

      closeMcpOAuthPopup(popup);
      oauthPopupRef.current = null;

      if (result.requiresPersonalAccessToken) {
        setTokenPromptProviderIds((current) => (
          current.includes(provider.providerId) ? current : [...current, provider.providerId]
        ));
        showToast(result.error || t('catalogTokenRequired'), 'error');
        return;
      }

      if (result.requiresOAuthClient) {
        setOauthClientPromptProviderIds((current) => (
          current.includes(provider.providerId) ? current : [...current, provider.providerId]
        ));
        if (result.oauthRedirectUri) {
          setProviderOAuthRedirectUris((current) => ({
            ...current,
            [provider.providerId]: result.oauthRedirectUri,
          }));
        }
        return;
      }

      if (result.connected) {
        setTokenPromptProviderIds((current) => current.filter((item) => item !== provider.providerId));
        setOauthClientPromptProviderIds((current) => current.filter((item) => item !== provider.providerId));
        setProviderTokens((current) => ({ ...current, [provider.providerId]: '' }));
        setProviderOAuthClients((current) => ({
          ...current,
          [provider.providerId]: { clientId: '', clientSecret: '' },
        }));
        await loadData();
        setSelectedServerId(result.serverId);
        setPaneMode('server');
        setWorkspaceTab('tools');
        showToast(t('catalogConnected', { name: provider.displayName }), 'success');
      }
    } catch (error) {
      closeMcpOAuthPopup(oauthPopupRef.current);
      oauthPopupRef.current = null;
      showToast(error instanceof Error ? error.message : t('catalogConnectFailed'), 'error');
    } finally {
      // Keep the button disabled while the vendor sign-in popup is still open.
      if (!oauthPopupRef.current) {
        setConnectingProviderId(null);
      }
    }
  };

  const copyCatalogOAuthRedirectUri = async (providerId: string, redirectUri: string) => {
    if (!redirectUri.trim() || !navigator.clipboard?.writeText) {
      showToast(t('catalogOAuthRedirectCopyFailed'), 'error');
      return;
    }

    try {
      await navigator.clipboard.writeText(redirectUri.trim());
      setCopiedOAuthRedirectProviderId(providerId);
    } catch {
      showToast(t('catalogOAuthRedirectCopyFailed'), 'error');
    }
  };

  const handleDisconnectProvider = async (providerId: string, server: McpServer) => {
    if (!canManage) return;

    setConnectingProviderId(providerId);
    try {
      await disconnectMcpOAuth(server.serverId);
      showToast(t('oauthDisconnected'), 'success');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('oauthFailed'), 'error');
    } finally {
      setConnectingProviderId(null);
    }
  };

  /* ---------------------------------------------------------- server CRUD */

  const handleCreateServer = async () => {
    if (!canManage) return;

    setCreatingServer(true);
    try {
      const created = await createMcpServer(toServerPayload(serverForm));
      setServers((current) => [...current, created]);
      setSelectedServerId(created.serverId);
      setServerForm(loadServerForm(created));
      setPaneMode('server');
      // An OAuth server still needs the account connected; anything else can go straight to tools.
      setWorkspaceTab(created.authType === 'oauth' ? 'settings' : 'tools');
      showToast(t('serverCreated'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToCreateServer'), 'error');
    } finally {
      setCreatingServer(false);
    }
  };

  const handleUpdateServerAuth = async () => {
    if (!canManage || !selectedServer) return;

    setSavingServer(true);
    try {
      const updated = await updateMcpServer(selectedServer.serverId, toServerPayload(serverForm));
      setServers((current) => current.map((server) => (
        server.serverId === updated.serverId ? updated : server
      )));
      showToast(t('serverUpdated'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToUpdateServer'), 'error');
    } finally {
      setSavingServer(false);
    }
  };

  const handleDeleteServer = async () => {
    if (!canManage || !selectedServer) return;
    if (!window.confirm(t('confirmDeleteServer'))) return;

    try {
      await deleteMcpServer(selectedServer.serverId);
      const remaining = servers.filter((server) => server.serverId !== selectedServer.serverId);
      setServers(remaining);
      setTools((current) => current.filter((tool) => tool.serverId !== selectedServer.serverId));
      setSelectedServerId(remaining[0]?.serverId ?? null);
      setPaneMode(remaining.length > 0 ? 'server' : 'catalog');
      resetWorkspaceSelections();
      showToast(t('serverDeleted'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDeleteServer'), 'error');
    }
  };

  const handleConnectOAuth = async () => {
    if (!canManage || !selectedServer) return;

    setConnectingOAuth(true);
    const popup = openMcpOAuthPopup();
    oauthPopupRef.current = popup;

    try {
      const authorizeUrl = await startMcpOAuth(selectedServer.serverId);
      if (!authorizeUrl) {
        closeMcpOAuthPopup(popup);
        oauthPopupRef.current = null;
        showToast(t('oauthFailed'), 'error');
        return;
      }

      if (sendPopupTo(popup, authorizeUrl)) {
        setOauthPopupOpen(true);
      } else {
        window.location.assign(authorizeUrl);
      }
    } catch (error) {
      closeMcpOAuthPopup(oauthPopupRef.current);
      oauthPopupRef.current = null;
      showToast(error instanceof Error ? error.message : t('oauthFailed'), 'error');
    } finally {
      // Keep the button disabled while the vendor sign-in popup is still open.
      if (!oauthPopupRef.current) {
        setConnectingOAuth(false);
      }
    }
  };

  const handleDisconnectOAuth = async () => {
    if (!canManage || !selectedServer) return;

    setConnectingOAuth(true);
    try {
      await disconnectMcpOAuth(selectedServer.serverId);
      showToast(t('oauthDisconnected'), 'success');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('oauthFailed'), 'error');
    } finally {
      setConnectingOAuth(false);
    }
  };

  /* -------------------------------------------------------- test/discover */

  const handleTestServer = async () => {
    if (!selectedServer) return;

    setTesting(true);
    try {
      const result = await testMcpServer(selectedServer.serverId);
      await loadData();
      if (result.success) {
        showToast(t('serverTested', { count: result.toolCount }), 'success');
      } else {
        showToast(result.error ?? t('serverTestFailed'), 'error');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('serverTestFailed'), 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleDiscover = async () => {
    if (!canManage || !selectedServer) return;

    setDiscovering(true);
    try {
      await discoverMcpServer(selectedServer.serverId);
      await loadData();
      showToast(t('toolsDiscovered'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDiscover'), 'error');
    } finally {
      setDiscovering(false);
    }
  };

  /* ----------------------------------------------------------- tool import */

  const toggleRemoteTool = (name: string) => {
    setSelectedRemoteTools((current) => (
      current.includes(name)
        ? current.filter((item) => item !== name)
        : [...current, name]
    ));
  };

  const toggleAllDiscoveredTools = () => {
    setSelectedRemoteTools(
      allDiscoveredSelected ? [] : discoveredTools.map((tool) => tool.name),
    );
  };

  const handleImportTools = async () => {
    if (!canManage || !selectedServer || selectedRemoteTools.length === 0) return;

    setImporting(true);
    try {
      const imported = await importMcpTools(
        selectedServer.serverId,
        selectedRemoteTools,
        importAsPublished,
        importAssignedUseCaseIds,
      );

      setTools((current) => {
        const byId = new Map(current.map((tool) => [tool.toolId, tool]));
        imported.forEach((tool) => byId.set(tool.toolId, tool));
        return Array.from(byId.values());
      });

      setSelectedRemoteTools([]);
      showToast(t('toolsImported', { count: imported.length }), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToImportTools'), 'error');
    } finally {
      setImporting(false);
    }
  };

  /* ------------------------------------------------------------ tool CRUD */

  const toggleImportedToolSelection = (toolId: string) => {
    setSelectedImportedToolIds((current) => (
      current.includes(toolId)
        ? current.filter((item) => item !== toolId)
        : [...current, toolId]
    ));
  };

  const toggleAllVisibleTools = () => {
    setSelectedImportedToolIds(
      allVisibleToolsSelected ? [] : visibleTools.map((tool) => tool.toolId),
    );
  };

  const handleBulkPublishTools = async (isPublished: boolean) => {
    if (!canManage || selectedImportedToolIds.length === 0) return;

    setBulkPublishing(true);
    try {
      const selectedTools = serverTools.filter((tool) => selectedImportedToolIds.includes(tool.toolId));
      const updates = await Promise.all(
        selectedTools.map((tool) => updateMcpTool(tool.toolId, {
          kernelFunctionName: tool.kernelFunctionName,
          displayName: tool.displayName,
          description: tool.description,
          isPublished,
          assignedUseCaseIds: bulkAssignedUseCaseIds,
        })),
      );

      setTools((current) => {
        const byId = new Map(current.map((item) => [item.toolId, item]));
        updates.forEach((tool) => byId.set(tool.toolId, tool));
        return Array.from(byId.values());
      });

      showToast(
        isPublished
          ? t('toolsBulkPublished', { count: updates.length })
          : t('toolsBulkUnpublished', { count: updates.length }),
        'success',
      );
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSaveTool'), 'error');
    } finally {
      setBulkPublishing(false);
    }
  };

  const handlePublishToggle = async (tool: McpTool, isPublished: boolean) => {
    if (!canManage) return;

    setSavingToolId(tool.toolId);
    try {
      const assignedUseCaseIds = previewTargetToolId === tool.toolId
        ? toolAssignedUseCaseIds
        : await getToolUseCaseAssignments(tool.functionId);

      const updated = await updateMcpTool(tool.toolId, {
        kernelFunctionName: tool.kernelFunctionName,
        displayName: tool.displayName,
        description: tool.description,
        isPublished,
        assignedUseCaseIds,
      });

      setTools((current) => current.map((item) => (item.toolId === updated.toolId ? updated : item)));
      showToast(isPublished ? t('toolPublished') : t('toolUnpublished'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSaveTool'), 'error');
    } finally {
      setSavingToolId(null);
    }
  };

  const handleSaveToolAssignments = async (tool: McpTool) => {
    if (!canManage) return;

    setSavingToolId(tool.toolId);
    try {
      const updated = await updateMcpTool(tool.toolId, {
        kernelFunctionName: tool.kernelFunctionName,
        displayName: tool.displayName,
        description: tool.description,
        isPublished: tool.isPublished,
        assignedUseCaseIds: toolAssignedUseCaseIds,
      });
      setTools((current) => current.map((item) => (item.toolId === updated.toolId ? updated : item)));
      showToast(t('toolAssignmentsSaved', { ns: 'tools', defaultValue: 'Use case assignments saved.' }), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSaveTool'), 'error');
    } finally {
      setSavingToolId(null);
    }
  };

  const handleDeleteTool = async (tool: McpTool) => {
    if (!canManage) return;
    if (!window.confirm(t('confirmDeleteTool'))) return;

    try {
      await deleteMcpTool(tool.toolId);
      setTools((current) => current.filter((item) => item.toolId !== tool.toolId));
      setSelectedImportedToolIds((current) => current.filter((item) => item !== tool.toolId));
      if (previewTargetToolId === tool.toolId) {
        setPreviewTargetToolId(null);
      }
      showToast(t('toolDeleted'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDeleteTool'), 'error');
    }
  };

  const handlePreviewTool = async (tool: McpTool) => {
    setPreviewingToolId(tool.toolId);
    setPreviewResult('');
    try {
      const result = await previewMcpTool(tool.toolId, previewArgs);
      setPreviewResult(result);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToPreviewTool'), 'error');
    } finally {
      setPreviewingToolId(null);
    }
  };

  const openPreviewDrawer = (tool: McpTool) => {
    setPreviewTargetToolId(tool.toolId);
    setPreviewArgs({});
    setPreviewResult('');
  };

  /* ----------------------------------------------------------- fragments */

  const renderStatusDot = (health: ConnectionHealth) => {
    const modifier = health === 'ok'
      ? styles.dotOk
      : health === 'warn'
        ? styles.dotWarn
        : health === 'error'
          ? styles.dotError
          : '';

    return <span className={`${styles.dot} ${modifier}`} />;
  };

  const renderRail = () => (
    <aside className={styles.rail}>
      <div className={styles.railHead}>
        <h3 className={styles.railTitle}>{t('railTitle')}</h3>
        <span className={styles.countPill}>{servers.length}</span>
      </div>

      {servers.length > 4 ? (
        <div className={styles.searchField}>
          <SearchIcon className={styles.searchFieldIcon} size={15} />
          <input
            className={styles.searchInput}
            value={serverQuery}
            placeholder={t('searchServers')}
            onChange={(event) => setServerQuery(event.target.value)}
          />
        </div>
      ) : null}

      {servers.length === 0 ? (
        <div className={styles.railEmpty}>{t('noServers')}</div>
      ) : (
        <div className={styles.railList}>
          {filteredServers.map((server) => {
            const isActive = paneMode === 'server' && selectedServerId === server.serverId;
            const toolCount = tools.filter((tool) => tool.serverId === server.serverId).length;

            return (
              <button
                key={server.serverId}
                type="button"
                className={`${styles.railItem} ${isActive ? styles.railItemActive : ''}`}
                onClick={() => handleSelectServer(server.serverId)}
              >
                <McpBrandMark
                  id={server.catalogProviderId || server.serverId}
                  name={server.displayName}
                  size="sm"
                />
                <span className={styles.railItemBody}>
                  <span className={styles.railItemName}>{server.displayName}</span>
                  <span className={styles.railItemMeta}>
                    {endpointHost(server.endpointUrl)}
                    {toolCount > 0 ? ` · ${t('importedCount', { count: toolCount })}` : ''}
                  </span>
                </span>
                {renderStatusDot(connectionHealth(server))}
              </button>
            );
          })}
          {filteredServers.length === 0 ? (
            <div className={styles.railEmpty}>{t('noSearchResults')}</div>
          ) : null}
        </div>
      )}

      {canManage ? (
        <div className={styles.railFooter}>
          <button
            type="button"
            className={`btn btn-secondary ${paneMode === 'catalog' ? styles.btnActive : ''}`}
            onClick={openCatalogPane}
          >
            <PlusIcon />
            <span>{t('addConnection')}</span>
          </button>
        </div>
      ) : null}
    </aside>
  );

  const renderCatalogPane = () => (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>{t('catalogTitle')}</h3>
          <p className={styles.cardIntro}>{t('catalogHelp')}</p>
        </div>
        {catalog.length > 6 ? (
          <div className={`${styles.searchField} ${styles.listToolbarSearch}`}>
            <SearchIcon className={styles.searchFieldIcon} size={15} />
            <input
              className={styles.searchInput}
              value={catalogQuery}
              placeholder={t('catalogSearchPlaceholder')}
              onChange={(event) => setCatalogQuery(event.target.value)}
            />
          </div>
        ) : null}
      </div>

      {filteredCatalog.length === 0 ? (
        <div className={styles.inlineEmpty}>
          {catalog.length === 0 ? t('catalogUnavailable') : t('noSearchResults')}
        </div>
      ) : (
        <div className={styles.catalogGrid}>
          {filteredCatalog.map((provider) => {
            const server = catalogServersByProvider.get(provider.providerId.toLowerCase());
            const isConnected = Boolean(server?.authConfigured || server?.oauthConnected);
            const isBusy = connectingProviderId === provider.providerId;
            const showTokenPrompt = tokenPromptProviderIds.includes(provider.providerId);
            const showOAuthClientPrompt = oauthClientPromptProviderIds.includes(provider.providerId);
            const token = (providerTokens[provider.providerId] ?? '').trim();
            const oauthClient = providerOAuthClients[provider.providerId] ?? { clientId: '', clientSecret: '' };
            const oauthRedirectUri = providerOAuthRedirectUris[provider.providerId] ?? '';

            return (
              <article
                key={provider.providerId}
                className={`${styles.providerCard} ${isConnected ? styles.providerCardConnected : ''}`}
              >
                <div className={styles.providerTop}>
                  <McpBrandMark id={provider.providerId} name={provider.displayName} />
                  <span>
                    <span className={styles.providerName}>{provider.displayName}</span>
                    <span className={styles.providerAuthMode}>
                      {provider.supportsPersonalAccessToken
                        ? t('authModeTokenOrSignIn')
                        : t('authModeVendorSignIn')}
                    </span>
                  </span>
                  {isConnected ? (
                    <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                      <CheckIcon size={12} />
                      {t('catalogConnectedBadge')}
                    </span>
                  ) : null}
                </div>

                <p className={styles.providerText}>{provider.description}</p>

                {canManage && !isConnected ? provider.fields.map((field) => {
                  const fieldValue = providerFields[provider.providerId]?.[field.name]
                    ?? (field.allowedValues.length > 0 ? (field.placeholder || field.allowedValues[0]) : '');

                  return (
                  <label key={field.name} className={styles.field}>
                    <span className={styles.fieldLabel}>
                      {t(`catalogField_${field.name}`, { defaultValue: field.name })}
                    </span>
                    {field.allowedValues.length > 0 ? (
                      <select
                        className="text-input"
                        value={fieldValue}
                        disabled={isBusy}
                        onChange={(event) => setProviderField(
                          provider.providerId,
                          field.name,
                          event.target.value,
                        )}
                      >
                        {field.allowedValues.map((value) => (
                          <option key={value} value={value}>
                            {t(`catalogField_${field.name}_${value}`, { defaultValue: value })}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="text-input"
                        value={fieldValue}
                        placeholder={field.placeholder}
                        disabled={isBusy}
                        onChange={(event) => setProviderField(
                          provider.providerId,
                          field.name,
                          event.target.value,
                        )}
                      />
                    )}
                  </label>
                  );
                }) : null}

                {canManage && showTokenPrompt ? (
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>{t('catalogTokenLabel')}</span>
                    <input
                      className="text-input"
                      type="password"
                      value={providerTokens[provider.providerId] ?? ''}
                      placeholder={t('catalogTokenPlaceholder')}
                      disabled={isBusy}
                      onChange={(event) => setProviderTokens((current) => ({
                        ...current,
                        [provider.providerId]: event.target.value,
                      }))}
                    />
                    <span className={styles.fieldHint}>{t('catalogTokenHint')}</span>
                  </label>
                ) : null}

                {canManage && showOAuthClientPrompt ? (
                  <div className={styles.oauthClientPrompt}>
                    <p className={styles.fieldHint}>{t('catalogOAuthClientRequired')}</p>
                    {oauthRedirectUri ? (
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>{t('catalogOAuthRedirectUri')}</span>
                        <div className={styles.copyRow}>
                          <input
                            className="text-input"
                            value={oauthRedirectUri}
                            readOnly
                            spellCheck={false}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={isBusy}
                            onClick={() => void copyCatalogOAuthRedirectUri(provider.providerId, oauthRedirectUri)}
                          >
                            {copiedOAuthRedirectProviderId === provider.providerId
                              ? t('catalogOAuthRedirectCopied')
                              : t('copy', { ns: 'common' })}
                          </button>
                        </div>
                        <span className={styles.fieldHint}>{t('catalogOAuthRedirectUriHint')}</span>
                      </div>
                    ) : null}
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>{t('catalogOAuthClientId')}</span>
                      <input
                        className="text-input"
                        value={oauthClient.clientId}
                        placeholder={t('catalogOAuthClientIdPlaceholder')}
                        autoComplete="off"
                        spellCheck={false}
                        disabled={isBusy}
                        onChange={(event) => setProviderOAuthClients((current) => ({
                          ...current,
                          [provider.providerId]: {
                            ...oauthClient,
                            clientId: event.target.value,
                          },
                        }))}
                      />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>{t('catalogOAuthClientSecret')}</span>
                      <input
                        className="text-input"
                        type="password"
                        value={oauthClient.clientSecret}
                        placeholder={t('catalogOAuthClientSecretPlaceholder')}
                        autoComplete="new-password"
                        disabled={isBusy}
                        onChange={(event) => setProviderOAuthClients((current) => ({
                          ...current,
                          [provider.providerId]: {
                            ...oauthClient,
                            clientSecret: event.target.value,
                          },
                        }))}
                      />
                    </label>
                  </div>
                ) : null}

                <div className={styles.providerActions}>
                  {canManage ? (
                    <button
                      type="button"
                      className={`btn btn-secondary ${styles.providerConnect}`}
                      disabled={isBusy}
                      onClick={() => void handleConnectProvider(provider, {
                        personalAccessToken: showTokenPrompt && token ? token : undefined,
                        oauthClientId: showOAuthClientPrompt ? oauthClient.clientId : undefined,
                        oauthClientSecret: showOAuthClientPrompt ? oauthClient.clientSecret : undefined,
                      })}
                    >
                      {isBusy
                        ? t('loading', { ns: 'common' })
                        : isConnected ? t('catalogReconnect') : t('catalogConnect')}
                    </button>
                  ) : null}
                  {server ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleSelectServer(server.serverId)}
                    >
                      {t('catalogOpen')}
                    </button>
                  ) : null}
                  {canManage && isConnected && server && server.authType === 'oauth' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isBusy}
                      onClick={() => void handleDisconnectProvider(provider.providerId, server)}
                    >
                      {t('oauthDisconnect')}
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {canManage ? (
        <div className={styles.catalogFooter}>
          <p className={styles.catalogFooterText}>{t('customServerCta')}</p>
          <button type="button" className="btn btn-secondary" onClick={startCreateServer}>
            <PlusIcon />
            <span>{t('addCustomServer')}</span>
          </button>
        </div>
      ) : null}
    </section>
  );

  const renderOAuthConnectionCard = () => {
    if (!selectedServer) {
      return null;
    }

    const usesOAuth = selectedServer.authType === 'oauth'
      && (isCatalogServer || serverForm.oauthGrantType === 'authorizationCode');

    if (!usesOAuth) {
      return null;
    }

    return (
      <div className={styles.connectionCard}>
        <div className={styles.connectionCardBody}>
          <span className={styles.checkRowLabel}>
            {selectedServer.oauthConnected ? t('oauthConnected') : t('oauthNotConnected')}
          </span>
          <span className={styles.fieldHint}>
            {isCatalogServer ? t('catalogManagedAuth') : t('oauthConnectHint')}
          </span>
        </div>
        {canManage ? (
          <div className={styles.bulkBarActions}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={connectingOAuth}
              onClick={() => void handleConnectOAuth()}
            >
              {connectingOAuth ? t('loading', { ns: 'common' }) : t('oauthConnect')}
            </button>
            {selectedServer.oauthConnected ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={connectingOAuth}
                onClick={() => void handleDisconnectOAuth()}
              >
                {t('oauthDisconnect')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCreatePane = () => (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>{t('createServer')}</h3>
          <p className={styles.cardIntro}>{t('createServerHint')}</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={openCatalogPane}>
          {t('browseCatalog')}
        </button>
      </div>

      <McpServerForm form={serverForm} server={null} mode="create" onChange={setServerForm} />

      <div className={styles.formActions}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={creatingServer || !serverForm.displayName.trim() || !serverForm.endpointUrl.trim()}
          onClick={() => void handleCreateServer()}
        >
          {creatingServer ? t('loading', { ns: 'common' }) : t('addServer')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={openCatalogPane}>
          {t('cancel', { ns: 'common' })}
        </button>
      </div>
    </section>
  );

  const renderSettingsTab = () => (
    <>
      <section className={styles.card}>
        <span className={styles.sectionLabel}>{t('sectionConnection')}</span>
        <McpServerForm
          form={serverForm}
          server={selectedServer}
          mode="edit"
          onChange={setServerForm}
        />
        {renderOAuthConnectionCard()}

        {canManage ? (
          <div className={styles.formActions}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={savingServer}
              onClick={() => void handleUpdateServerAuth()}
            >
              {savingServer ? t('loading', { ns: 'common' }) : t('saveServerAuth')}
            </button>
          </div>
        ) : null}
      </section>

      {canManage ? (
        <section className={`${styles.card} ${styles.dangerCard}`}>
          <div className={styles.cardHead}>
            <div>
              <h3 className={styles.cardTitle}>{t('sectionDanger')}</h3>
              <p className={styles.cardIntro}>{t('dangerHelp')}</p>
            </div>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void handleDeleteServer()}
            >
              <TrashIcon />
              <span>{t('deleteServer')}</span>
            </button>
          </div>
        </section>
      ) : null}
    </>
  );

  const renderImportCard = () => {
    if (discoveredTools.length === 0) {
      return null;
    }

    return (
      <section className={`${styles.card} ${styles.importCard}`} aria-busy={importing}>
        {importing ? (
          <>
            <span className={styles.busyBar} aria-hidden="true" />
            <div className={styles.busyOverlay} role="status" aria-live="polite">
              <Spinner size={36} />
              <div className={styles.busyCopy}>
                <strong>{t('importingTools', { count: selectedRemoteTools.length })}</strong>
                <span>{t('importingToolsHelp')}</span>
              </div>
            </div>
          </>
        ) : null}
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>
              {t('availableToImport')}
              <span className={styles.titleCount}>{newlyDiscoveredCount}</span>
            </h3>
            <p className={styles.cardIntro}>{t('availableToImportHelp')}</p>
          </div>
          {canManage ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={importing}
              onClick={toggleAllDiscoveredTools}
            >
              {allDiscoveredSelected
                ? t('deselectAllAvailable', { ns: 'tools', defaultValue: 'Deselect all' })
                : t('selectAllAvailable', { ns: 'tools', defaultValue: 'Select all' })}
            </button>
          ) : null}
        </div>

        <div className={styles.discoverGrid}>
          {discoveredTools.map((tool) => {
            const isSelected = selectedRemoteTools.includes(tool.name);
            const alreadyImported = importedRemoteNames.has(tool.name);

            return (
              <label
                key={tool.name}
                className={`${styles.discoverItem} ${isSelected ? styles.discoverItemSelected : ''}`}
              >
                {canManage ? (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={importing}
                    onChange={() => toggleRemoteTool(tool.name)}
                  />
                ) : <span />}
                <span>
                  <span className={styles.discoverItemName}>{tool.name}</span>
                  {alreadyImported ? (
                    <span className={`${styles.badge} ${styles.badgeInline}`}>
                      <CheckIcon size={11} />
                      {t('alreadyImported')}
                    </span>
                  ) : null}
                  {tool.readOnlyHint === false ? (
                    <span className={`${styles.badge} ${styles.badgeInline} ${styles.badgeWarn}`}>
                      {t('writeAccess')}
                    </span>
                  ) : tool.readOnlyHint === true ? (
                    <span className={`${styles.badge} ${styles.badgeInline}`}>
                      {t('readAccess')}
                    </span>
                  ) : null}
                  {tool.description ? (
                    <span className={styles.discoverItemText}>{tool.description}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>

        {canManage ? (
          <div className={styles.importOptions}>
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={importAsPublished}
                disabled={importing}
                onChange={(event) => setImportAsPublished(event.target.checked)}
              />
              <span>
                <span className={styles.checkRowLabel}>{t('importAsPublished')}</span>
                <span className={styles.checkRowHint}>{t('importAsPublishedHelp')}</span>
              </span>
            </label>

            <button
              type="button"
              className={styles.disclosure}
              disabled={importing}
              onClick={() => setShowImportOptions((current) => !current)}
            >
              <span>{t('useCaseOptions')}</span>
              <ChevronDownIcon
                size={14}
                className={`${styles.disclosureIcon} ${showImportOptions ? styles.disclosureIconOpen : ''}`}
              />
            </button>

            {showImportOptions ? (
              <ToolUseCaseAssignment
                selectedUseCaseIds={importAssignedUseCaseIds}
                disabled={importing}
                isPublished={importAsPublished}
                onChange={setImportAssignedUseCaseIds}
              />
            ) : null}

            <div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={importing || selectedRemoteTools.length === 0}
                onClick={() => void handleImportTools()}
              >
                {importing ? <span className={styles.btnSpinner} aria-hidden="true" /> : <ImportIcon />}
                <span>
                  {importing
                    ? t('importingTools', { count: selectedRemoteTools.length })
                    : t('importSelectedCount', { count: selectedRemoteTools.length })}
                </span>
              </button>
            </div>
          </div>
        ) : null}
      </section>
    );
  };

  const renderToolsTab = () => {
    if (!selectedServer) {
      return null;
    }

    const hasTools = serverTools.length > 0;

    return (
      <>
        {renderImportCard()}

        <section className={styles.card}>
          <div className={styles.cardHead}>
            <div>
              <h3 className={styles.cardTitle}>{t('publishedTools')}</h3>
              <p className={styles.cardIntro}>{t('publishedToolsHelp')}</p>
            </div>
          </div>

          {hasTools ? (
            <>
              <div className={styles.listToolbar}>
                <div className={`${styles.searchField} ${styles.listToolbarSearch}`}>
                  <SearchIcon className={styles.searchFieldIcon} size={15} />
                  <input
                    className={styles.searchInput}
                    value={toolQuery}
                    placeholder={t('searchTools')}
                    onChange={(event) => setToolQuery(event.target.value)}
                  />
                </div>
                <div className={styles.segmented}>
                  {([
                    { id: 'all' as ToolFilter, label: t('filterAll'), count: serverTools.length },
                    {
                      id: 'published' as ToolFilter,
                      label: t('filterPublished'),
                      count: serverTools.filter((tool) => tool.isPublished).length,
                    },
                    {
                      id: 'draft' as ToolFilter,
                      label: t('filterDraft'),
                      count: serverTools.filter((tool) => !tool.isPublished).length,
                    },
                  ]).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`${styles.segment} ${toolFilter === option.id ? styles.segmentActive : ''}`}
                      onClick={() => setToolFilter(option.id)}
                    >
                      {option.label}
                      <span className={styles.segmentCount}>{option.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.toolTable}>
                <div className={`${styles.toolRow} ${styles.toolRowHead}`}>
                  {canManage ? (
                    <input
                      type="checkbox"
                      checked={allVisibleToolsSelected}
                      disabled={visibleTools.length === 0 || bulkPublishing}
                      aria-label={t('selectAllAvailable', { ns: 'tools', defaultValue: 'Select all' })}
                      onChange={toggleAllVisibleTools}
                    />
                  ) : <span />}
                  <span>{t('toolName')}</span>
                  <span className={styles.toolCellKernel}>{t('kernelFunctionName')}</span>
                  <span>{t('published')}</span>
                  <span />
                </div>

                {visibleTools.length === 0 ? (
                  <div className={styles.inlineEmpty}>{t('noSearchResults')}</div>
                ) : visibleTools.map((tool) => {
                  const isSelected = selectedImportedToolIds.includes(tool.toolId);
                  const isSaving = savingToolId === tool.toolId;
                  const memberPluginNames = pluginNamesByFunctionId.get(tool.functionId) ?? [];

                  return (
                    <div
                      key={tool.toolId}
                      className={`${styles.toolRow} ${isSelected ? styles.toolRowSelected : ''}`}
                    >
                      {canManage ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={bulkPublishing || isSaving}
                          aria-label={tool.displayName}
                          onChange={() => toggleImportedToolSelection(tool.toolId)}
                        />
                      ) : <span />}

                      <span className={styles.toolCellMain}>
                        <span className={styles.toolName}>{tool.displayName}</span>
                        <span className={styles.toolMeta}>{tool.remoteToolName}</span>
                        {memberPluginNames.length > 0 ? (
                          <span className={styles.pluginChips}>
                            {memberPluginNames.map((pluginName) => (
                              <span
                                key={pluginName}
                                className={`${styles.badge} ${styles.badgeAccent} ${styles.pluginChip}`}
                                title={t('membershipTooltip', {
                                  ns: 'toolPlugins',
                                  names: memberPluginNames.join(', '),
                                })}
                              >
                                {pluginName}
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </span>

                      <span className={styles.toolCellKernel}>{tool.kernelFunctionName}</span>

                      <label className={styles.switch}>
                        <input
                          className={styles.switchInput}
                          type="checkbox"
                          checked={tool.isPublished}
                          disabled={!canManage || isSaving}
                          aria-label={t('published')}
                          onChange={(event) => void handlePublishToggle(tool, event.target.checked)}
                        />
                        <span className={styles.switchTrack} />
                      </label>

                      <span className={styles.toolRowActions}>
                        <button
                          type="button"
                          className={`${styles.iconButton} ${previewTargetToolId === tool.toolId ? styles.iconButtonActive : ''}`}
                          title={t('openPreview')}
                          aria-label={t('openPreview')}
                          onClick={() => openPreviewDrawer(tool)}
                        >
                          <PlayIcon size={15} />
                        </button>
                        {canManage ? (
                          <button
                            type="button"
                            className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                            title={t('deleteTool')}
                            aria-label={t('deleteTool')}
                            onClick={() => void handleDeleteTool(tool)}
                          >
                            <TrashIcon size={15} />
                          </button>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>

              {canManage && selectedImportedToolIds.length > 0 ? (
                <div className={styles.bulkBar}>
                  <div className={styles.bulkBarHead}>
                    <span className={styles.bulkBarCount}>
                      {t('selectedCount', { count: selectedImportedToolIds.length })}
                    </span>
                    <button
                      type="button"
                      className={styles.disclosure}
                      onClick={() => setShowBulkOptions((current) => !current)}
                    >
                      <span>{t('useCaseOptions')}</span>
                      <ChevronDownIcon
                        size={14}
                        className={`${styles.disclosureIcon} ${showBulkOptions ? styles.disclosureIconOpen : ''}`}
                      />
                    </button>
                  </div>

                  {showBulkOptions ? (
                    <ToolUseCaseAssignment
                      selectedUseCaseIds={bulkAssignedUseCaseIds}
                      disabled={bulkPublishing}
                      isPublished
                      onChange={setBulkAssignedUseCaseIds}
                    />
                  ) : null}

                  <div className={styles.bulkBarActions}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={bulkPublishing}
                      onClick={() => void handleBulkPublishTools(true)}
                    >
                      {bulkPublishing
                        ? t('loading', { ns: 'common' })
                        : t('publishSelected', { count: selectedImportedToolIds.length })}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={bulkPublishing}
                      onClick={() => void handleBulkPublishTools(false)}
                    >
                      {t('unpublishSelected', { count: selectedImportedToolIds.length })}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={bulkPublishing}
                      onClick={() => setSelectedImportedToolIds([])}
                    >
                      {t('clearSelection')}
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}><ToolboxIcon size={22} /></span>
              <span className={styles.emptyTitle}>{t('toolsEmptyTitle')}</span>
              <p className={styles.emptyText}>{t('noTools')}</p>
              {canManage ? (
                <div className={styles.emptyActions}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={discovering}
                    onClick={() => void handleDiscover()}
                  >
                    <RadarIcon />
                    <span>{discovering ? t('loading', { ns: 'common' }) : t('discoverTools')}</span>
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </section>
      </>
    );
  };

  const renderServerPane = () => {
    if (!selectedServer) {
      return (
        <section className={styles.empty}>
          <span className={styles.emptyIcon}><PlugIcon size={22} /></span>
          <span className={styles.emptyTitle}>{t('selectServerTitle')}</span>
          <p className={styles.emptyText}>{t('selectServer')}</p>
          {canManage ? (
            <div className={styles.emptyActions}>
              <button type="button" className="btn btn-primary" onClick={openCatalogPane}>
                <PlusIcon />
                <span>{t('addConnection')}</span>
              </button>
              <button type="button" className="btn btn-secondary" onClick={startCreateServer}>
                {t('addCustomServer')}
              </button>
            </div>
          ) : null}
        </section>
      );
    }

    const health = connectionHealth(selectedServer);
    const testFailed = selectedServer.lastTestStatus === 'failed';

    return (
      <>
        <header className={styles.hero}>
          <McpBrandMark
            id={selectedServer.catalogProviderId || selectedServer.serverId}
            name={selectedServer.displayName}
            size="lg"
          />

          <div className={styles.heroBody}>
            <h3 className={styles.heroTitle}>{selectedServer.displayName}</h3>
            <span className={styles.heroEndpoint}>{selectedServer.endpointUrl}</span>
            <div className={styles.heroBadges}>
              <span className={`${styles.badge} ${styles.badgeMono}`}>
                {selectedServer.transport === 'sseLegacy'
                  ? t('transportSseLegacy')
                  : t('transportStreamableHttpBadge')}
              </span>

              {selectedServer.authType === 'oauth' ? (
                <span className={`${styles.badge} ${selectedServer.oauthConnected ? styles.badgeSuccess : styles.badgeWarn}`}>
                  <ShieldIcon size={12} />
                  {selectedServer.oauthConnected ? t('oauthConnected') : t('oauthNotConnected')}
                </span>
              ) : selectedServer.authConfigured ? (
                <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                  <ShieldIcon size={12} />
                  {t('authConfigured')}
                </span>
              ) : (
                <span className={styles.badge}>{t('authNoneBadge')}</span>
              )}

              {health === 'ok' ? (
                <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                  <PulseIcon size={12} />
                  {t('testPassed')}
                </span>
              ) : null}
              {testFailed ? (
                <span className={`${styles.badge} ${styles.badgeDanger}`}>
                  <AlertIcon size={12} />
                  {t('testFailed')}
                </span>
              ) : null}

              {selectedServer.discoveredTools.length > 0 ? (
                <span className={styles.badge}>
                  {t('discoveredCount', { count: selectedServer.discoveredTools.length })}
                </span>
              ) : null}
              {isCatalogServer ? (
                <span className={`${styles.badge} ${styles.badgeAccent}`}>{t('managedBadge')}</span>
              ) : null}
            </div>
          </div>

          <div className={styles.heroActions}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={testing}
              onClick={() => void handleTestServer()}
            >
              <PulseIcon />
              <span>{testing ? t('loading', { ns: 'common' }) : t('testConnection')}</span>
            </button>
            {canManage ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={discovering}
                onClick={() => void handleDiscover()}
              >
                <RadarIcon />
                <span>{discovering ? t('loading', { ns: 'common' }) : t('discoverTools')}</span>
              </button>
            ) : null}
          </div>
        </header>

        {selectedServer.lastTestMessage ? (
          <div className={`${styles.notice} ${testFailed ? styles.noticeError : styles.noticeOk}`}>
            <span className={styles.noticeIcon}>
              {testFailed ? <AlertIcon size={15} /> : <CheckIcon size={15} />}
            </span>
            <span>{selectedServer.lastTestMessage}</span>
          </div>
        ) : null}

        {canManage ? (
          <nav className={styles.segmented} aria-label={t('workspaceTabsLabel')}>
            <button
              type="button"
              className={`${styles.segment} ${workspaceTab === 'tools' ? styles.segmentActive : ''}`}
              onClick={() => setWorkspaceTab('tools')}
            >
              <ToolboxIcon size={15} />
              {t('tabTools')}
              {serverTools.length > 0 ? (
                <span className={styles.segmentCount}>{serverTools.length}</span>
              ) : null}
            </button>
            <button
              type="button"
              className={`${styles.segment} ${workspaceTab === 'settings' ? styles.segmentActive : ''}`}
              onClick={() => setWorkspaceTab('settings')}
            >
              <ShieldIcon size={15} />
              {t('tabSettings')}
            </button>
          </nav>
        ) : null}

        {workspaceTab === 'settings' && canManage ? renderSettingsTab() : renderToolsTab()}
      </>
    );
  };

  const renderPreviewDrawer = () => {
    if (!previewTargetTool) {
      return null;
    }

    const isRunning = previewingToolId === previewTargetTool.toolId;
    const isSaving = savingToolId === previewTargetTool.toolId;

    return (
      <div
        className={styles.drawerOverlay}
        role="presentation"
        onClick={() => setPreviewTargetToolId(null)}
      >
        <div
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-label={previewTargetTool.displayName}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.drawerHead}>
            <div>
              <h3 className={styles.drawerTitle}>{previewTargetTool.displayName}</h3>
              <p className={styles.drawerSubtitle}>{previewTargetTool.kernelFunctionName}</p>
            </div>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t('closePreview')}
              onClick={() => setPreviewTargetToolId(null)}
            >
              <CloseIcon size={15} />
            </button>
          </div>

          <div className={styles.drawerBody}>
            {previewTargetTool.description ? (
              <p className={styles.cardIntro}>{previewTargetTool.description}</p>
            ) : null}

            <div>
              <span className={styles.sectionLabel}>{t('previewParameters')}</span>
              {previewTargetTool.parameters.length === 0 ? (
                <p className={styles.fieldHint}>{t('previewNoParams')}</p>
              ) : (
                <div className={`${styles.formGrid} ${styles.formGridTight}`}>
                  {previewTargetTool.parameters.map((parameter) => (
                    <label key={parameter.name} className={`${styles.field} ${styles.fieldWide}`}>
                      <span className={styles.fieldLabel}>
                        {parameter.name}
                        {parameter.isRequired ? ' *' : ''}
                      </span>
                      <input
                        className="text-input"
                        value={previewArgs[parameter.name] ?? ''}
                        placeholder={parameter.description || parameter.example}
                        onChange={(event) => setPreviewArgs((current) => ({
                          ...current,
                          [parameter.name]: event.target.value,
                        }))}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>

            {previewResult ? (
              <div>
                <span className={styles.sectionLabel}>{t('previewResultLabel')}</span>
                <pre className={styles.previewOutput}>{previewResult}</pre>
              </div>
            ) : null}

            <ToolUseCaseAssignment
              selectedUseCaseIds={toolAssignedUseCaseIds}
              disabled={!canManage || isSaving}
              isPublished={previewTargetTool.isPublished}
              onChange={setToolAssignedUseCaseIds}
            />
          </div>

          <div className={styles.drawerFoot}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isRunning}
              onClick={() => void handlePreviewTool(previewTargetTool)}
            >
              <PlayIcon />
              <span>{isRunning ? t('loading', { ns: 'common' }) : t('runPreview')}</span>
            </button>
            {canManage ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={isSaving}
                onClick={() => void handleSaveToolAssignments(previewTargetTool)}
              >
                {isSaving
                  ? t('loading', { ns: 'common' })
                  : t('saveUseCaseAssignments', { ns: 'tools', defaultValue: 'Save use case assignments' })}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPreviewTargetToolId(null)}
            >
              {t('closePreview')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* -------------------------------------------------------------- render */

  if (loading) {
    return (
      <div className={styles.pageLoading}>
        <Spinner />
        <span>{t('loading', { ns: 'common' })}</span>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageBar}>
        <div className={styles.statStrip}>
          <span className={styles.stat}>
            <PlugIcon className={styles.statIcon} size={15} />
            <span className={styles.statValue}>{servers.length}</span>
            {t('statConnections')}
          </span>
          <span className={styles.stat}>
            <ToolboxIcon className={styles.statIcon} size={15} />
            <span className={styles.statValue}>{tools.length}</span>
            {t('statImportedTools')}
          </span>
          <span className={styles.stat}>
            <CheckIcon className={styles.statIcon} size={15} />
            <span className={styles.statValue}>{publishedToolCount}</span>
            {t('statPublishedTools')}
          </span>
        </div>

        {canManage ? (
          <div className={styles.pageBarActions}>
            <button type="button" className="btn btn-primary" onClick={openCatalogPane}>
              <PlusIcon />
              <span>{t('addConnection')}</span>
            </button>
            <button type="button" className="btn btn-secondary" onClick={startCreateServer}>
              {t('addCustomServer')}
            </button>
          </div>
        ) : null}
      </div>

      <div className={styles.layout}>
        {renderRail()}

        <div className={styles.pane}>
          {paneMode === 'catalog' ? renderCatalogPane() : null}
          {paneMode === 'create' ? renderCreatePane() : null}
          {paneMode === 'server' ? renderServerPane() : null}
        </div>
      </div>

      {renderPreviewDrawer()}
    </div>
  );
};
